import React, { useState, useEffect } from "react";
import Playlist from "./Playlist";
import "./PlaylistView.css";
import { playlistsService } from "../services/playlistsService";
import { playlistTrackReviewsService } from "../services/playlistTrackReviewsService";
import { playlistPermissionsService } from "../services/playlistPermissionsService";
import { useAuth } from "../contexts/AuthContext";

const PlaylistView = ({ playlists, setPlaylists, openPlaylistModal }) => {
  const { user } = useAuth();
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [sharedPlaylists, setSharedPlaylists] = useState([]);
  const [playingUrl, setPlayingUrl] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    isPublic: true
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingPlaylist, setSharingPlaylist] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [sharePermission, setSharePermission] = useState("comment");
  const [sharedUsers, setSharedUsers] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingTrack, setReviewingTrack] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    comment: ""
  });
  const [trackRatings, setTrackRatings] = useState({});

  // Cargar playlists compartidas cuando cambie el usuario
  useEffect(() => {
    const loadSharedPlaylists = async () => {
      if (user?.id) {
        try {
          const shared = await playlistPermissionsService.getSharedWithUser(user.id);
          setSharedPlaylists(shared);
        } catch (error) {
          console.error("Error cargando playlists compartidas:", error);
        }
      }
    };

    loadSharedPlaylists();
  }, [user]);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      const menu = document.querySelector('.playlist-dropdown-menu-detail.show');
      if (menu && !menu.closest('.playlist-menu-container-detail').contains(e.target)) {
        menu.classList.remove('show');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Función para ver canciones de una playlist
  const handleViewSongs = (playlist) => {
    setSelectedPlaylist(playlist);
  };

  // Función para cerrar vista de canciones
  const handleCloseView = () => {
    setSelectedPlaylist(null);
    if (audioElement) {
      audioElement.pause();
      setAudioElement(null);
      setPlayingUrl(null);
    }
  };

  // Función para reproducir preview
  const handlePlay = (url, trackId) => {
    if (audioElement) {
      audioElement.pause();
    }

    if (playingUrl === trackId) {
      setPlayingUrl(null);
      setAudioElement(null);
    } else {
      const audio = new Audio(url);
      audio.play();
      setAudioElement(audio);
      setPlayingUrl(trackId);
      audio.onended = () => {
        setPlayingUrl(null);
        setAudioElement(null);
      };
    }
  };

  // Función para eliminar/desvincular playlist
  const handleDeletePlaylist = async (playlist) => {
    // Si es una playlist compartida, desvincular
    if (playlist.isShared || playlist.permission) {
      const confirm = window.confirm(
        `¿Deseas desvincular la playlist "${playlist.name}"? Ya no aparecerá en tu lista.`
      );

      if (!confirm) return;

      try {
        await playlistPermissionsService.unlinkSelfFromPlaylist(playlist.id, user.id);
        const updatedShared = await playlistPermissionsService.getSharedWithUser(user.id);
        setSharedPlaylists(updatedShared);
        alert(`✅ Playlist "${playlist.name}" desvinculada`);

        if (selectedPlaylist?.id === playlist.id) {
          setSelectedPlaylist(null);
        }
      } catch (error) {
        console.error("Error desvinculando playlist:", error);
        alert("Error al desvincular la playlist. Por favor intenta de nuevo.");
      }
      return;
    }

    // Si es propia, eliminar
    const confirm = window.confirm(
      `¿Estás seguro de que quieres eliminar la playlist "${playlist.name}"?`
    );

    if (!confirm) return;

    try {
      await playlistsService.deletePlaylist(playlist.id, user.id);
      const updatedPlaylists = await playlistsService.getUserPlaylists(user.id);
      setPlaylists(updatedPlaylists);
      alert(`✅ Playlist "${playlist.name}" eliminada`);

      // Si estábamos viendo esta playlist, cerrar la vista
      if (selectedPlaylist?.id === playlist.id) {
        setSelectedPlaylist(null);
      }
    } catch (error) {
      console.error("Error eliminando playlist:", error);
      alert("Error al eliminar la playlist. Por favor intenta de nuevo.");
    }
  };

  // Función para eliminar canción de playlist
  const handleRemoveSong = async (trackId) => {
    if (!selectedPlaylist) return;

    const confirm = window.confirm("¿Eliminar esta canción de la playlist?");
    if (!confirm) return;

    try {
      await playlistsService.removeTrackFromPlaylist(selectedPlaylist.id, trackId);
      const updatedPlaylists = await playlistsService.getUserPlaylists(user.id);
      setPlaylists(updatedPlaylists);

      // Actualizar la playlist seleccionada
      const updatedSelected = updatedPlaylists.find(p => p.id === selectedPlaylist.id);
      setSelectedPlaylist(updatedSelected);

      alert("Canción eliminada");
    } catch (error) {
      console.error("Error eliminando canción:", error);
      alert("Error al eliminar la canción. Por favor intenta de nuevo.");
    }
  };

  // Función para abrir modal de edición
  const handleEdit = (playlist) => {
    setEditingPlaylist(playlist);
    setEditForm({
      name: playlist.name,
      description: playlist.description || "",
      isPublic: playlist.isPublic
    });
    setShowEditModal(true);
  };

  // Función para cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingPlaylist(null);
    setEditForm({
      name: "",
      description: "",
      isPublic: true
    });
  };

  // Función para guardar cambios de edición
  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      alert("El nombre de la playlist no puede estar vacío");
      return;
    }

    try {
      await playlistsService.updatePlaylist(editingPlaylist.id, user.id, {
        name: editForm.name,
        description: editForm.description,
        isPublic: editForm.isPublic,
        image: editingPlaylist.image
      });

      const updatedPlaylists = await playlistsService.getUserPlaylists(user.id);
      setPlaylists(updatedPlaylists);

      // Si estamos viendo esta playlist, actualizarla
      if (selectedPlaylist?.id === editingPlaylist.id) {
        const updatedSelected = updatedPlaylists.find(p => p.id === editingPlaylist.id);
        setSelectedPlaylist(updatedSelected);
      }

      alert("Playlist actualizada");
      handleCloseEditModal();
    } catch (error) {
      console.error("Error actualizando playlist:", error);
      alert("Error al actualizar la playlist. Por favor intenta de nuevo.");
    }
  };

  // Función para compartir
  const handleShare = async (playlist) => {
    setSharingPlaylist(playlist);
    setShareEmail("");
    setSharePermission("comment");

    // Cargar usuarios que ya tienen acceso
    try {
      const users = await playlistPermissionsService.getPlaylistPermissions(playlist.id);
      setSharedUsers(users);
    } catch (error) {
      console.error("Error cargando usuarios compartidos:", error);
      setSharedUsers([]);
    }

    setShowShareModal(true);
  };

  // Función para cerrar modal de compartir
  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setSharingPlaylist(null);
    setShareEmail("");
    setSharePermission("comment");
    setSharedUsers([]);
  };

  // Función para agregar usuario por email
  const handleShareWithUser = async () => {
    if (!shareEmail.trim()) {
      alert("Por favor ingresa un email");
      return;
    }

    if (!shareEmail.includes('@')) {
      alert("Por favor ingresa un email válido");
      return;
    }

    try {
      await playlistPermissionsService.sharePlaylist(
        sharingPlaylist.id,
        user.id,
        shareEmail.trim(),
        sharePermission
      );

      // Recargar lista de usuarios compartidos
      const users = await playlistPermissionsService.getPlaylistPermissions(sharingPlaylist.id);
      setSharedUsers(users);
      setShareEmail("");
      alert(`✅ Playlist compartida con ${shareEmail}`);
    } catch (error) {
      console.error("Error compartiendo playlist:", error);
      alert(error.message || "Error al compartir la playlist. Verifica que el usuario exista.");
    }
  };

  // Función para remover acceso de un usuario
  const handleRemoveUser = async (userId) => {
    const confirm = window.confirm("¿Deseas remover el acceso de este usuario?");
    if (!confirm) return;

    try {
      await playlistPermissionsService.revokePermission(sharingPlaylist.id, userId, user.id);
      const users = await playlistPermissionsService.getPlaylistPermissions(sharingPlaylist.id);
      setSharedUsers(users);
      alert("✅ Acceso removido");
    } catch (error) {
      console.error("Error removiendo acceso:", error);
      alert("Error al remover acceso");
    }
  };

  // Función para copiar enlace de playlist
  const handleCopyLink = () => {
    const link = `${window.location.origin}/playlist/${sharingPlaylist.id}`;
    navigator.clipboard.writeText(link);
    alert("Enlace copiado al portapapeles");
  };

  // Función para abrir modal de review
  const handleOpenReview = async (track) => {
    setReviewingTrack(track);

    // Cargar review existente si hay
    if (user?.id && track.id) {
      try {
        const existingReview = await playlistTrackReviewsService.getUserReview(track.id, user.id);
        if (existingReview) {
          setReviewForm({
            rating: existingReview.rating,
            comment: existingReview.comment || ""
          });
        } else {
          setReviewForm({ rating: 0, comment: "" });
        }
      } catch (error) {
        console.error("Error cargando review:", error);
        setReviewForm({ rating: 0, comment: "" });
      }
    }

    setShowReviewModal(true);
  };

  // Función para cerrar modal de review
  const handleCloseReview = () => {
    setShowReviewModal(false);
    setReviewingTrack(null);
    setReviewForm({ rating: 0, comment: "" });
  };

  // Función para guardar review
  const handleSaveReview = async () => {
    if (!reviewForm.rating || reviewForm.rating === 0) {
      alert("Por favor selecciona una calificación");
      return;
    }

    if (!user?.id || !reviewingTrack?.id) {
      alert("Error: No se puede guardar el review");
      return;
    }

    try {
      await playlistTrackReviewsService.createOrUpdateReview(
        reviewingTrack.id,
        user.id,
        reviewForm.rating,
        reviewForm.comment
      );

      // Recargar ratings
      await loadTrackRatings();

      alert("Calificación guardada");
      handleCloseReview();
    } catch (error) {
      console.error("Error guardando review:", error);
      alert("Error al guardar la calificación");
    }
  };

  // Cargar ratings de todas las canciones de la playlist
  const loadTrackRatings = async () => {
    if (!selectedPlaylist?.songs) return;

    const ratings = {};
    for (const song of selectedPlaylist.songs) {
      if (song.id) {
        try {
          // Obtener TODAS las reviews con información de usuarios
          const allReviews = await playlistTrackReviewsService.getAllReviewsWithUsers(song.id);

          // Calcular promedio
          const average = allReviews.length > 0
            ? allReviews.reduce((acc, r) => acc + r.rating, 0) / allReviews.length
            : 0;

          ratings[song.id] = {
            average: average,
            total: allReviews.length,
            reviews: allReviews // Guardar todas las reviews
          };
        } catch (error) {
          console.error(`Error cargando rating para canción ${song.id}:`, error);
        }
      }
    }
    setTrackRatings(ratings);
  };

  // Cargar ratings cuando cambia la playlist seleccionada
  useEffect(() => {
    if (selectedPlaylist) {
      loadTrackRatings();
    }
  }, [selectedPlaylist]);

  return (
    <div className="playlist-view-container">
      {/* Vista de lista de playlists */}
      {!selectedPlaylist && (
        <>
          <div className="playlist-view-header">
            <h1>Mis Playlists</h1>
            <button
              className="btn-create-new-playlist"
              onClick={() => openPlaylistModal(null)}
            >
              + Nueva Playlist
            </button>
          </div>

          {playlists.length === 0 && sharedPlaylists.length === 0 ? (
            <div className="empty-playlists">
              <div className="empty-icon-text">♪</div>
              <h2>No tienes playlists aún</h2>
              <p>Crea tu primera playlist y comienza a agregar tus canciones favoritas</p>
              <button
                className="btn-create-first-playlist"
                onClick={() => openPlaylistModal(null)}
              >
                + Crear Primera Playlist
              </button>
            </div>
          ) : (
            <>
              {playlists.length > 0 && (
                <>
                  <h2 className="playlists-section-title">Mis Playlists</h2>
                  <div className="playlists-grid">
                    {playlists.map((playlist) => (
                      <Playlist
                        key={playlist.id}
                        data={playlist}
                        onViewSongs={handleViewSongs}
                        onDelete={handleDeletePlaylist}
                        onEdit={handleEdit}
                        onShare={handleShare}
                        isOwner={true}
                        isShared={false}
                      />
                    ))}
                  </div>
                </>
              )}

              {sharedPlaylists.length > 0 && (
                <>
                  <h2 className="playlists-section-title">Compartidas Conmigo</h2>
                  <div className="playlists-grid">
                    {sharedPlaylists.map((playlist) => (
                      <Playlist
                        key={playlist.id}
                        data={playlist}
                        onViewSongs={handleViewSongs}
                        onDelete={handleDeletePlaylist}
                        onEdit={playlist.permission === 'collaborate' ? handleEdit : null}
                        onShare={null}
                        isOwner={false}
                        isShared={true}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Vista de canciones de una playlist */}
      {selectedPlaylist && (
        <div className="playlist-songs-view">
          <div className="playlist-detail-header">
            <button className="btn-back" onClick={handleCloseView}>
              ← Volver
            </button>
            <div className="playlist-detail-info">
              {selectedPlaylist.songs?.[0]?.image || selectedPlaylist.image ? (
                <img
                  src={selectedPlaylist.songs?.[0]?.image || selectedPlaylist.image}
                  alt={selectedPlaylist.name}
                  className="playlist-detail-image"
                />
              ) : (
                <div className="playlist-detail-image-placeholder">
                  <span className="playlist-icon-large">{selectedPlaylist.name.charAt(0).toUpperCase()}</span>
                </div>
              )}
              <div className="playlist-detail-meta">
                <div className="playlist-title-container">
                  <h1>{selectedPlaylist.name}</h1>
                  <div className="playlist-menu-container-detail">
                    <button
                      className="playlist-menu-button-detail"
                      onClick={(e) => {
                        e.stopPropagation();
                        const menuOpen = document.querySelector('.playlist-dropdown-menu-detail.show');
                        if (menuOpen) {
                          menuOpen.classList.remove('show');
                        } else {
                          e.currentTarget.nextElementSibling.classList.add('show');
                        }
                      }}
                    >
                      ...
                    </button>
                    <div className="playlist-dropdown-menu-detail">
                      <button className="menu-item" onClick={() => handleEdit(selectedPlaylist)}>
                        Editar
                      </button>
                      <button className="menu-item" onClick={() => handleShare(selectedPlaylist)}>
                        Compartir
                      </button>
                      <button className="menu-item menu-item-danger" onClick={() => {
                        handleDeletePlaylist(selectedPlaylist);
                      }}>
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
                {selectedPlaylist.description && (
                  <p className="playlist-detail-description">
                    {selectedPlaylist.description}
                  </p>
                )}
                <div className="playlist-detail-stats">
                  <span>{selectedPlaylist.songs?.length || 0} canciones</span>
                  <span className={`visibility-badge ${selectedPlaylist.isPublic ? "public" : "private"}`}>
                    {selectedPlaylist.isPublic ? "Pública" : "Privada"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="playlist-songs-list">
            {selectedPlaylist.songs?.length === 0 ? (
              <div className="empty-songs">
                <p>Esta playlist está vacía</p>
                <p className="empty-hint">
                  Busca canciones en el Feed o en la sección de Búsqueda y agrégalas a esta playlist
                </p>
              </div>
            ) : (
              selectedPlaylist.songs.map((song, index) => {
                const trackId = `${selectedPlaylist.id}-${song.id}`;
                const rating = trackRatings[song.id];

                return (
                  <div key={trackId} className="playlist-song-item" data-has-reviews={rating && rating.total > 0}>
                    <div className="song-image-container">
                      {song.image ? (
                        <img
                          src={song.image}
                          alt={song.title}
                          className="song-image"
                        />
                      ) : (
                        <div className="song-image-placeholder">♪</div>
                      )}
                      {song.previewUrl && (
                        <div
                          className="song-play-overlay"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlay(song.previewUrl, trackId);
                          }}
                        >
                          <div className="song-play-button">
                            {playingUrl === trackId ? "⏸" : "▶"}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="song-info">
                      <div className="song-title">{song.title}</div>
                      <div className="song-artist">{song.artist}</div>
                    </div>

                    <div className="song-actions">
                      {/* Avatares de usuarios que calificaron - aparecen en hover */}
                      {rating && rating.total > 0 && (
                        <div className="song-reviews-avatars">
                          {rating.reviews.map((review, idx) => (
                            <div
                              key={review.id || idx}
                              className="review-avatar-wrapper"
                            >
                              <div className="review-rating-badge">
                                ⭐ {review.rating}
                              </div>
                              {review.profiles?.avatar_url && review.profiles.avatar_url.trim() !== '' ? (
                                <img
                                  src={review.profiles.avatar_url}
                                  alt={review.profiles.username || review.profiles.email}
                                  className="review-user-avatar"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const fallback = document.createElement('div');
                                    fallback.className = 'review-user-avatar-placeholder';
                                    fallback.textContent = (review.profiles?.username?.[0] || review.profiles?.email?.charAt(0) || '?').toUpperCase();
                                    e.target.parentElement.appendChild(fallback);
                                  }}
                                />
                              ) : (
                                <div className="review-user-avatar-placeholder">
                                  {(review.profiles?.username?.[0] || review.profiles?.email?.charAt(0) || '?').toUpperCase()}
                                </div>
                              )}
                              {review.comment && (
                                <div className="review-comment-tooltip">
                                  <div className="review-tooltip-header">
                                    <strong>{review.profiles?.username || review.profiles?.email?.split('@')[0] || 'Usuario'}</strong>
                                    <span>⭐ {review.rating}</span>
                                  </div>
                                  <div className="review-tooltip-comment">
                                    {review.comment}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        className="btn-song-action btn-play-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (song.previewUrl) {
                            handlePlay(song.previewUrl, trackId);
                          }
                        }}
                        disabled={!song.previewUrl}
                        title={playingUrl === trackId ? "Pausar" : "Reproducir"}
                      >
                        {playingUrl === trackId ? "⏸" : "▶"}
                      </button>
                      <button
                        className="btn-song-action btn-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSong(song.id);
                        }}
                        title="Eliminar"
                      >
                        ×
                      </button>
                    </div>
                    {/* Botón de estrella para calificar */}
                    <button
                      className="btn-rate-song"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenReview(song);
                      }}
                      title="Calificar y comentar"
                    >
                      ⭐
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Modal de Edición */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Editar Playlist</h2>
            <div className="modal-form">
              <label>
                Nombre
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Nombre de la playlist"
                />
              </label>
              <label>
                Descripción
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Descripción (opcional)"
                  rows="3"
                />
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={editForm.isPublic}
                  onChange={(e) => setEditForm({ ...editForm, isPublic: e.target.checked })}
                />
                Playlist pública
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn-modal btn-cancel" onClick={handleCloseEditModal}>
                Cancelar
              </button>
              <button className="btn-modal btn-save" onClick={handleSaveEdit}>
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Compartir */}
      {showShareModal && (
        <div className="modal-overlay" onClick={handleCloseShareModal}>
          <div className="modal-content modal-share" onClick={(e) => e.stopPropagation()}>
            <h2>Compartir Playlist</h2>
            <p className="share-description">
              Comparte "{sharingPlaylist?.name}" con otros usuarios
            </p>

            {/* Sección para agregar usuario */}
            <div className="share-add-user-section">
              <h3>Agregar Usuario</h3>
              <div className="share-user-form">
                <input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="email@ejemplo.com"
                  className="share-email-input"
                />
                <select
                  value={sharePermission}
                  onChange={(e) => setSharePermission(e.target.value)}
                  className="share-permission-select"
                >
                  <option value="comment">📝 Solo Calificador - Puede ver, comentar y calificar</option>
                  <option value="collaborate">✏️ Colaborador - Puede editar, agregar y eliminar canciones</option>
                </select>
                <button
                  className="btn-share-add"
                  onClick={handleShareWithUser}
                >
                  Agregar
                </button>
              </div>
            </div>

            {/* Lista de usuarios con acceso */}
            {sharedUsers.length > 0 && (
              <div className="shared-users-list">
                <h3>Usuarios con Acceso ({sharedUsers.length})</h3>
                {sharedUsers.map((sharedUser) => (
                  <div key={sharedUser.id} className="shared-user-item">
                    <div className="shared-user-info">
                      {sharedUser.profiles?.avatar_url && sharedUser.profiles.avatar_url.trim() !== '' ? (
                        <img
                          src={sharedUser.profiles.avatar_url}
                          alt={sharedUser.profiles.username || sharedUser.profiles.email}
                          className="shared-user-avatar"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.className = 'shared-user-avatar-placeholder';
                            fallback.textContent = (sharedUser.profiles?.username?.[0] || sharedUser.profiles?.email?.charAt(0) || '?').toUpperCase();
                            e.target.parentElement.appendChild(fallback);
                          }}
                        />
                      ) : (
                        <div className="shared-user-avatar-placeholder">
                          {(sharedUser.profiles?.username?.[0] || sharedUser.profiles?.email?.charAt(0) || '?').toUpperCase()}
                        </div>
                      )}
                      <div className="shared-user-details">
                        <div className="shared-user-name">
                          {sharedUser.profiles?.username || sharedUser.profiles?.email?.split('@')[0]}
                        </div>
                        <div className="shared-user-email">{sharedUser.profiles?.email}</div>
                        <div className="shared-user-permission">
                          {sharedUser.permission_type === 'comment' && '📝 Calificador'}
                          {sharedUser.permission_type === 'collaborate' && '✏️ Colaborador'}
                        </div>
                      </div>
                    </div>
                    <button
                      className="btn-remove-user"
                      onClick={() => handleRemoveUser(sharedUser.user_id)}
                      title="Remover acceso"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Enlace público (solo si es pública) */}
            {sharingPlaylist?.isPublic && (
              <div className="share-link-section">
                <h3>Enlace Público</h3>
                <p className="share-link-description">
                  Esta playlist es pública. Cualquiera con el enlace puede verla.
                </p>
                <div className="share-link-container">
                  <input
                    type="text"
                    value={`${window.location.origin}/playlist/${sharingPlaylist?.id}`}
                    readOnly
                    className="share-link-input"
                  />
                  <button className="btn-copy" onClick={handleCopyLink}>
                    Copiar
                  </button>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-modal btn-cancel" onClick={handleCloseShareModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Calificación y Comentario */}
      {showReviewModal && reviewingTrack && (
        <div className="modal-overlay" onClick={handleCloseReview}>
          <div className="modal-content modal-review" onClick={(e) => e.stopPropagation()}>
            <h2>Calificar Canción</h2>

            <div className="review-track-info">
              {reviewingTrack.image && (
                <img src={reviewingTrack.image} alt={reviewingTrack.title} className="review-track-image" />
              )}
              <div>
                <div className="review-track-title">{reviewingTrack.title}</div>
                <div className="review-track-artist">{reviewingTrack.artist}</div>
              </div>
            </div>

            <div className="modal-form">
              <label>
                Calificación
                <div className="star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`star-button ${reviewForm.rating >= star ? 'active' : ''}`}
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                    >
                      ⭐
                    </button>
                  ))}
                </div>
              </label>

              <label>
                Comentario (opcional)
                <textarea
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  placeholder="Escribe tu comentario sobre esta canción..."
                  rows="4"
                />
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn-modal btn-cancel" onClick={handleCloseReview}>
                Cancelar
              </button>
              <button className="btn-modal btn-save" onClick={handleSaveReview}>
                Guardar Calificación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistView;
